class StorageService {
  constructor() {
    // Private cache for frequently accessed items (scalable for perf)
    this.cache = new Map();
  }

  /**
   * Set item in localStorage (stringify if object)
   * @param {string} key - Storage key
   * @param {any} value - Value to store (auto-stringify if not string)
   */
  set(key, value) {
    try {
      const storedValue = typeof value === 'object' ? JSON.stringify(value) : value;
      localStorage.setItem(key, storedValue);
      this.cache.set(key, value);  // Cache for quick get
    } catch (error) {
      console.warn(`Failed to set ${key} in localStorage:`, error);
    }
  }

  /**
   * Get item from localStorage with existence check and type conversion
   * @param {string} key - Storage key
   * @param {any} defaultValue - Fallback if missing (default null)
   * @param {string} type - Conversion type: 'string' (default), 'int', 'number', 'boolean', 'json'
   * @return {any|null} Converted value or default
   */
  get(key, defaultValue = null, type = 'string') {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;

    try {
      let value = item;
      switch (type) {
        case 'json':
          value = JSON.parse(item);
          break;
        case 'int':
          value = parseInt(item, 10);
          break;
        case 'number':
          value = Number(item);
          break;
        case 'boolean':
          value = JSON.parse(item.toLowerCase());  // 'true'/'false' to boolean
          break;
        case 'string':
        default:
          value = item;
          break;
      }
      if (isNaN(value) && (type === 'int' || type === 'number')) return defaultValue;  // Fallback on invalid
      this.cache.set(key, value);  // Cache converted
      return value;
    } catch (error) {
      console.warn(`Failed to parse ${key} from localStorage:`, error);
      return defaultValue;
    }
  }

  /**
   * Check if item exists in localStorage
   * @param {string} key - Storage key
   * @return {boolean}
   */
  has(key) {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   */
  remove(key) {
    localStorage.removeItem(key);
    this.cache.delete(key);
  }

  /**
   * Clear all items (scalable for selective clear in future)
   */
  clear() {
    localStorage.clear();
    this.cache.clear();
  }
}

// Singleton instance for global use
const localStorageService = new StorageService();
export default localStorageService;
